import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Alert,
  InputAdornment,
  alpha,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import Navigation from './Navigation';
import DashboardPermission from './permissions/DashboardPermission';
import CalendarPermissions from './permissions/CalendarPermissions';
import LocationsPermissions from './permissions/LocationsPermissions';
import SchedulePermissions from './permissions/SchedulePermissions';
import SalesPermissions from './permissions/CRMPermissions';
import ProformaInvoicePermissions from './permissions/proforma-invoice-permissions/ProformaInvoicePermissions';
import ProformaInvoiceWorkflowPermissions from './permissions/proforma-invoice-permissions/ProformaInvoiceWorkflowPermissions';
import ProformaInvoiceConfirmationPermissions from './permissions/proforma-invoice-permissions/ProformaInvoiceConfirmationPermissions';
import ProductsPermissions from './permissions/ProductsPermissions';
import FormulationsPermissions from './permissions/FormulationsPermissions';
import SuppliersPermissions from './permissions/VendorPermissions';
import InventoryPermissions from './permissions/inventory-permissions/InventoryPermissions';
import MaterialQualityCheckPermissions from './permissions/inventory-permissions/MaterialQualityCheckPermissions';
import ImportAndExportPermissions from './permissions/inventory-permissions/ImportAndExportPermissions';
import ProductionPlanningPermissions from './permissions/ProductionPlanningPermissions';
import BatchProductionPermissions from './permissions/production-batches-permissions/BatchProductionPermissions';
import BatchMixingPermissions from './permissions/production-batches-permissions/BatchMixingPermissions';
import BatchLogPermissions from './permissions/production-batches-permissions/BatchLogPermissions';
import LineProductionPermissions from './permissions/line-production-permissions/LineProductionPermissions';
import LineProductionAssemblyPermissions from './permissions/line-production-permissions/LineProductionAssemblyPermissions';
import LineProductionLogPermissions from './permissions/line-production-permissions/LineProductionLogPermissions';
import UserManagementPermissions from './permissions/settings-permissions/UserManagementPermissions';
import CompanySettingsPermissions from './permissions/settings-permissions/CompanySettingsPermissions';
import EmployeesPermissions from './permissions/EmployeesPermissions';
import ExpensesPermissions from './permissions/ExpensesPermissions';
import AccountingPermissions from './permissions/AccountingPermissions';
import { supabase } from '../../../../../supabaseClient';
import { usePermissions } from '../../../../../utils/permissions';

const CreateRoleDialogue = ({ 
  open, 
  onClose, 
  permissions,
  onCreateRole,
  editingRole = null,
  onUpdateRole = null
}) => {
  const theme = useTheme();
  const { companyId } = usePermissions();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [selectedCategory, setSelectedCategory] = useState('dashboard');
  
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: {}
  });

  const isEditMode = editingRole !== null;

  // Load editing role data when dialog opens
  useEffect(() => {
    if (isEditMode && editingRole && open) {
      // Immediately set form data and go to step 3
      setRoleForm({
        name: editingRole.name,
        display_name: editingRole.display_name,
        description: editingRole.description || '',
        permissions: {}
      });
      setActiveStep(2);
      setCompletedSteps(new Set([0, 1]));
      
      // Load permissions in background
      loadRoleForEdit();
    } else if (!isEditMode && open) {
      // Reset form for create mode
      setRoleForm({
        name: '',
        display_name: '',
        description: '',
        permissions: {}
      });
      setActiveStep(0);
      setCompletedSteps(new Set());
      setSelectedCategory('dashboard');
    }
  }, [isEditMode, editingRole, open]);

  const loadRoleForEdit = async () => {
    try {
      // Load role permissions
      const { data: rolePermissions, error } = await supabase
        .from('role_permissions')
        .select(`
          permission_id,
          can_read,
          can_write,
          can_delete
        `)
        .eq('role_id', editingRole.id);

      if (error) throw error;

      // Convert to permissions format
      const permissionsObj = {};
      rolePermissions?.forEach(rp => {
        permissionsObj[rp.permission_id] = {
          read: rp.can_read,
          write: rp.can_write,
          delete: rp.can_delete
        };
      });

      // Update only permissions, keep existing form data
      setRoleForm(prev => ({
        ...prev,
        permissions: permissionsObj
      }));
    } catch (error) {
      console.error('Error loading role for edit:', error);
      setAlert({
        open: true,
        message: 'Failed to load role data',
        severity: 'error'
      });
    }
  };

  const isStep1Valid = () => {
    return roleForm.name.trim() && roleForm.display_name.trim();
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (isStep1Valid()) {
        setCompletedSteps(prev => new Set([...prev, 0]));
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      setCompletedSteps(prev => new Set([...prev, 1]));
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleStepClick = (step) => {
    if (step === 0 || (step === 1 && completedSteps.has(0)) || (step === 2 && completedSteps.has(1))) {
      setActiveStep(step);
    }
  };

  const handleCreateRole = async () => {
    try {
      if (isEditMode) {
        await handleUpdateRole();
        return;
      }

      // Check if role name already exists before creating
      const { data: existingRoles } = await supabase.from('roles')
        .select('id, name, display_name, is_system_role')
        .or(`company_id.eq.${companyId},is_system_role.eq.true`);

      const roleNameLower = roleForm.name.trim().toLowerCase();
      const displayNameLower = roleForm.display_name.trim().toLowerCase();

      const blacklistedNames = [
        'super_admin', 'super admin', 'superadmin', 'super_administrator', 'super administrator', 'superadministrator'
      ];
      
      const errors = [];

      // Check role name
      if (blacklistedNames.includes(roleNameLower)) {
        errors.push('Role name conflicts with a system role');
      } else {
        const roleNameExists = existingRoles?.some(role => {
          const existingNameLower = role.name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
          const inputRoleNameNormalized = roleNameLower.replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
          return existingNameLower === inputRoleNameNormalized || 
                 role.name.toLowerCase() === roleNameLower ||
                 role.display_name.toLowerCase() === roleNameLower;
        });
        
        if (roleNameExists) {
          errors.push('Role name already exists');
        }
      }

      // Check display name
      if (blacklistedNames.includes(displayNameLower)) {
        errors.push('Display name conflicts with a system role');
      } else {
        const displayNameExists = existingRoles?.some(role => {
          const existingDisplayNameLower = role.display_name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
          const inputDisplayNameNormalized = displayNameLower.replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
          return existingDisplayNameLower === inputDisplayNameNormalized || 
                 role.name.toLowerCase() === displayNameLower ||
                 role.display_name.toLowerCase() === displayNameLower;
        });
        
        if (displayNameExists) {
          errors.push('Display name already exists');
        }
      }

      if (errors.length > 0) {
        setAlert({
          open: true,
          message: errors.join('. '),
          severity: 'error'
        });
        return;
      }

      await onCreateRole(roleForm);
      handleClose();
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || 'Failed to create role',
        severity: 'error'
      });
    }
  };

  const handleUpdateRole = async () => {
    try {
      if (!onUpdateRole) {
        throw new Error('Update function not provided');
      }

      await onUpdateRole(editingRole.id, roleForm);
      handleClose();
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || 'Failed to update role',
        severity: 'error'
      });
    }
  };

  const handleClose = () => {
    // Clear form data and reset dialog state
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permissions: {}
    });
    setActiveStep(0);
    setCompletedSteps(new Set());
    setPermissionSearchTerm('');
    setAlert({ open: false, message: '', severity: 'info' });
    onClose();
  };

  return (
    <Dialog
      open={open}
      disableScrollLock
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '95vh',
          width: '90vw',
          maxWidth: '1400px'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500, 
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: 'primary.main'
      }}>
{isEditMode ? 'Edit Role' : 'Create Custom Role'}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 0, height: 'calc(100% - 140px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflow: 'hidden' }}>
          {alert.open && (
            <Alert 
              severity={alert.severity} 
              onClose={() => setAlert({ ...alert, open: false })}
              sx={{ 
                mb: 2,
                borderRadius: 2,
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                '& .MuiAlert-message': { 
                  fontWeight: 500,
                  color: '#000000 !important',
                  fontSize: '1rem'
                },
                '& .MuiAlert-icon': {
                  color: '#f44336 !important'
                }
              }}
            >
              {alert.message}
            </Alert>
          )}

{activeStep !== 2 && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={(activeStep / 2) * 100} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.grey[300], 0.5),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    bgcolor: theme.palette.primary.main
                  }
                }} 
              />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontWeight: 500 }}>
                Step {activeStep + 1} of 3
              </Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Stepper activeStep={activeStep} orientation="vertical" sx={{ 
              flex: activeStep === 2 ? 0 : 1,
              overflow: activeStep === 2 ? 'visible' : 'auto',
              '& .MuiStepConnector-root': {
                display: activeStep === 2 ? 'none' : 'block'
              }
            }}>
            <Step>
              <StepLabel 
                onClick={() => handleStepClick(0)}
                sx={{ 
                  cursor: 'pointer',
                  display: activeStep === 2 ? 'none' : 'flex',
                  '& .MuiStepLabel-label': { 
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    color: activeStep === 0 ? 'primary.main' : 'text.primary'
                  }
                }}
              >
                Basic Information
              </StepLabel>
              <StepContent>
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Role Name *"
                        value={roleForm.name}
                        onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                        error={!roleForm.name.trim() && completedSteps.has(0)}
                        helperText={
                          (!roleForm.name.trim() && completedSteps.has(0)) ? "Role name is required" : 
                          "Internal name (lowercase, underscores)"
                        }
                        sx={{ 
                          '& .MuiInputLabel-root': { fontWeight: 500 },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: alpha(theme.palette.grey[50], 0.8),
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.primary.main, 0.5)
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: '2px'
                            }
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#000000 !important',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Display Name *"
                        value={roleForm.display_name}
                        onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                        error={!roleForm.display_name.trim() && completedSteps.has(0)}
                        helperText={
                          (!roleForm.display_name.trim() && completedSteps.has(0)) ? "Display name is required" : 
                          "Name shown to users"
                        }
                        sx={{ 
                          '& .MuiInputLabel-root': { fontWeight: 500 },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            background: alpha(theme.palette.grey[50], 0.8),
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.primary.main, 0.5)
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: '2px'
                            }
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#000000 !important',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Grid>

                    
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 500, 
                          color: 'info.main',
                          mb: 1
                        }}>
                          Hierarchy Level Assignment
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          This role will be automatically assigned the next available hierarchy level. You can drag and drop to rearrange hierarchy levels after creation.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={!isStep1Valid()}
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.2,
                        fontWeight: 500,
                        background: !isStep1Valid() ? alpha(theme.palette.grey[400], 0.5) : `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: !isStep1Valid() ? `1px solid ${alpha(theme.palette.grey[400], 0.5)}` : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        color: !isStep1Valid() ? alpha(theme.palette.grey[600], 0.8) : 'text.secondary',
                        cursor: !isStep1Valid() ? 'not-allowed' : 'pointer',
                        '&:hover': !isStep1Valid() ? {} : { 
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                          transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                          backgroundColor: alpha(theme.palette.grey[400], 0.5),
                          color: alpha(theme.palette.grey[600], 0.8),
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel 
                onClick={() => handleStepClick(1)}
                sx={{ 
                  cursor: completedSteps.has(0) ? 'pointer' : 'default',
                  display: activeStep === 2 ? 'none' : 'flex',
                  '& .MuiStepLabel-label': { 
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    color: activeStep === 1 ? 'primary.main' : completedSteps.has(0) ? 'text.primary' : 'text.disabled'
                  }
                }}
              >
                Description (Optional)
              </StepLabel>
              <StepContent>
                <Box sx={{ mt: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                    placeholder="Describe the role's responsibilities and scope..."
                    sx={{ 
                      '& .MuiInputLabel-root': { fontWeight: 500 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: alpha(theme.palette.grey[50], 0.8),
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.5)
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      }
                    }}
                  />
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      onClick={handleBack}
                      sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1.2,
                        fontWeight: 500,
                        color: 'text.secondary',
                        '&:hover': {
                          background: alpha(theme.palette.grey[400], 0.1)
                        }
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.2,
                        fontWeight: 500,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        color: 'text.secondary',
                        '&:hover': { 
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel 
                onClick={() => handleStepClick(2)}
                sx={{ 
                  cursor: completedSteps.has(1) ? 'pointer' : 'default',
                  display: activeStep === 2 ? 'none' : 'flex',
                  '& .MuiStepLabel-label': { 
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    color: activeStep === 2 ? 'primary.main' : completedSteps.has(1) ? 'text.primary' : 'text.disabled'
                  }
                }}
              >
                Permissions
              </StepLabel>
              <StepContent>
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, flex: 1 }}>
                      Select the permissions for this role. Permissions are optional.
                    </Typography>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<AdminIcon />}
                      onClick={() => {
                        const newPermissions = {};
                        permissions.forEach(permission => {
                          newPermissions[permission.id] = {
                            read: true,
                            write: true,
                            delete: true
                          };
                        });
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      sx={{
                        px: 3,
                        py: 1,
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Enable Administrator Privileges
                    </Button>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ 
                      mb: 1, 
                      fontWeight: 500, 
                      color: 'text.secondary',
                      fontSize: '0.9rem'
                    }}>
                      Search
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Search permissions..."
                      value={permissionSearchTerm}
                      onChange={(e) => setPermissionSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        sx: { 
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          height: 56,
                          '&:hover': {
                            border: `1px solid ${alpha(theme.palette.grey[600], 0.8)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${alpha(theme.palette.grey[700], 0.9)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          fontWeight: 500,
                          py: 2,
                          px: 2
                        },
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                          '& fieldset': {
                            border: 'none'
                          },
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ 
                    height: activeStep === 2 ? 'calc(100vh - 300px)' : '600px',
                    display: 'flex',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    background: 'white',
                    overflow: 'hidden'
                  }}>
                    <Navigation 
                      selectedCategory={selectedCategory}
                      onCategoryChange={setSelectedCategory}
                    />
                    
                    <Box sx={{ 
                      flex: 1,
                      overflowY: 'auto',
                      p: 2,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: alpha(theme.palette.grey[300], 0.3),
                        borderRadius: '3px',
                        margin: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: alpha(theme.palette.grey[500], 0.5),
                        borderRadius: '3px',
                        '&:hover': {
                          background: alpha(theme.palette.grey[600], 0.7)
                        }
                      },
                    }}>
                      {((!permissionSearchTerm && selectedCategory === 'dashboard') || (permissionSearchTerm && ('dashboard'.includes(permissionSearchTerm.toLowerCase()) || 'dashboard'.toLowerCase().includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-dashboard" sx={{ mb: 3 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 2,
                            py: 2.5,
                            px: 3,
                            backgroundColor: alpha('#4CAF50', 0.12),
                            border: `2px solid ${alpha('#4CAF50', 0.3)}`,
                            borderRadius: 3,
                            boxShadow: `0 2px 8px ${alpha('#4CAF50', 0.15)}`
                          }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: '#4CAF50',
                              fontSize: '1.4rem',
                              letterSpacing: '0.5px'
                            }}>
                              Dashboard
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const dashboardPermissions = permissions.filter(p => p.name && p.name.startsWith('dashboard.'));
                                  dashboardPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: '#f44336',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.05)
                                  }
                                }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const dashboardPermissions = permissions.filter(p => p.name && p.name.startsWith('dashboard.'));
                                  dashboardPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.grey[500], 0.05)
                                  }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                          
                          <DashboardPermission
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'calendar') || (permissionSearchTerm && ('calendar'.includes(permissionSearchTerm.toLowerCase()) || 'calendar'.toLowerCase().includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-calendar" sx={{ mb: 3 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 2,
                            py: 2.5,
                            px: 3,
                            backgroundColor: alpha('#FF5722', 0.12),
                            border: `2px solid ${alpha('#FF5722', 0.3)}`,
                            borderRadius: 3,
                            boxShadow: `0 2px 8px ${alpha('#FF5722', 0.15)}`
                          }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: '#FF5722',
                              fontSize: '1.4rem',
                              letterSpacing: '0.5px'
                            }}>
                              Calendar
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const calendarPermissions = permissions.filter(p => p.name && p.name.startsWith('calendar.'));
                                  calendarPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: '#f44336',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.05)
                                  }
                                }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const calendarPermissions = permissions.filter(p => p.name && p.name.startsWith('calendar.'));
                                  calendarPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.grey[500], 0.05)
                                  }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                          
                          <CalendarPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'locations') || (permissionSearchTerm && ('locations'.includes(permissionSearchTerm.toLowerCase()) || 'equipment'.includes(permissionSearchTerm.toLowerCase()) || 'maintenance'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-locations" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#3F51B5', 0.12),
                          border: `2px solid ${alpha('#3F51B5', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#3F51B5', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#3F51B5',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Locations
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const locationsPermissions = permissions.filter(p => p.name && p.name.startsWith('locations.'));
                                locationsPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const locationsPermissions = permissions.filter(p => p.name && p.name.startsWith('locations.'));
                                locationsPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <LocationsPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'supply_chain') || (permissionSearchTerm && ('supply'.includes(permissionSearchTerm.toLowerCase()) || 'chain'.includes(permissionSearchTerm.toLowerCase()) || 'suppliers'.includes(permissionSearchTerm.toLowerCase()) || 'vendors'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-supply_chain" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#9C27B0', 0.12),
                          border: `2px solid ${alpha('#9C27B0', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#9C27B0', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#9C27B0',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Vendors
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Enable only supply chain suppliers permissions
                                const supplyChainPermissions = permissions.filter(p => p.name && p.name.includes('supply_chain.'));
                                supplyChainPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Disable only supply chain suppliers permissions
                                const supplyChainPermissions = permissions.filter(p => p.name && p.name.includes('supply_chain.'));
                                supplyChainPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                        <SuppliersPermissions
                          permissions={permissions}
                          roleForm={roleForm}
                          setRoleForm={setRoleForm}
                        />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'expenses') || (permissionSearchTerm && ('expenses'.includes(permissionSearchTerm.toLowerCase()) || 'accounts'.includes(permissionSearchTerm.toLowerCase()) || 'categories'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-expenses" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#C62828', 0.12),
                          border: `2px solid ${alpha('#C62828', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#C62828', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#C62828',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Expenses
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const expensesPermissions = permissions.filter(p => p.name && p.name.startsWith('expenses.'));
                                expensesPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#C62828',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#C62828', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const expensesPermissions = permissions.filter(p => p.name && p.name.startsWith('expenses.'));
                                expensesPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <ExpensesPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {/* Accounting Section */}
                      {((!permissionSearchTerm && selectedCategory === 'accounting') || (permissionSearchTerm && ('accounting'.includes(permissionSearchTerm.toLowerCase()) || 'banking'.includes(permissionSearchTerm.toLowerCase()) || 'receivables'.includes(permissionSearchTerm.toLowerCase()) || 'payables'.includes(permissionSearchTerm.toLowerCase()) || 'forecast'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-accounting" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#1B5E20', 0.12),
                          border: `2px solid ${alpha('#1B5E20', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#1B5E20', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#1B5E20',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Accounting
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const accountingPermissions = permissions.filter(p => p.name && p.name.includes('accounting.'));
                                accountingPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const accountingPermissions = permissions.filter(p => p.name && p.name.includes('accounting.'));
                                accountingPermissions.forEach(permission => {
                                  delete newPermissions[permission.id];
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: theme.palette.grey[500],
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <AccountingPermissions
                            permissions={permissions}
                            selectedPermissions={roleForm.permissions}
                            onPermissionChange={(permissionId, field, value) => {
                              const newPermissions = { ...roleForm.permissions };
                              if (!newPermissions[permissionId]) {
                                newPermissions[permissionId] = { read: false, write: false, delete: false };
                              }
                              // Map field names
                              if (field === 'can_read') newPermissions[permissionId].read = value;
                              if (field === 'can_write') newPermissions[permissionId].write = value;
                              if (field === 'can_delete') newPermissions[permissionId].delete = value;
                              setRoleForm({ ...roleForm, permissions: newPermissions });
                            }}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'sales') || (permissionSearchTerm && ('sales'.includes(permissionSearchTerm.toLowerCase()) || 'customers'.includes(permissionSearchTerm.toLowerCase()) || 'invoices'.includes(permissionSearchTerm.toLowerCase()) || 'overview'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-sales" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#2196F3', 0.12),
                          border: `2px solid ${alpha('#2196F3', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#2196F3', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#2196F3',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            CRM
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const salesPermissions = permissions.filter(p => p.name && p.name.includes('sales.'));
                                salesPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const salesPermissions = permissions.filter(p => p.name && p.name.includes('sales.'));
                                salesPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <SalesPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />

                          {/* Purchase Orders subsection */}
                          <Box sx={{ mb: 3, mt: 3, ml: 2 }} id="proforma-invoices-section">
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              mb: 2,
                              py: 2,
                              px: 2.5,
                              backgroundColor: alpha('#E91E63', 0.12),
                              borderRadius: 3,
                              border: `2px solid ${alpha('#E91E63', 0.3)}`,
                              boxShadow: `0 2px 6px ${alpha('#E91E63', 0.15)}`
                            }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 500, 
                                color: '#E91E63',
                                fontSize: '1.15rem',
                                letterSpacing: '0.3px'
                              }}>
                                Orders
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    const purchaseOrdersPermissions = permissions.filter(p => p.name && p.name.includes('sales.purchase_orders.'));
                                    purchaseOrdersPermissions.forEach(permission => {
                                      newPermissions[permission.id] = {
                                        read: true,
                                        write: true,
                                        delete: true
                                      };
                                    });
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: '#f44336',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha('#f44336', 0.05)
                                    }
                                  }}
                                >
                                  All
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    const purchaseOrdersPermissions = permissions.filter(p => p.name && p.name.includes('sales.purchase_orders.'));
                                    purchaseOrdersPermissions.forEach(permission => {
                                      newPermissions[permission.id] = {
                                        read: false,
                                        write: false,
                                        delete: false
                                      };
                                    });
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.grey[500], 0.05)
                                    }
                                  }}
                                >
                                  None
                                </Button>
                              </Box>
                            </Box>
                            
                            <ProformaInvoicePermissions
                              permissions={permissions}
                              roleForm={roleForm}
                              setRoleForm={setRoleForm}
                            />

                            {/* Purchase Order Workflow sub-subsection */}
                            <Box sx={{ mb: 2, mt: 3, ml: 2 }} id="purchase-order-workflow-section">
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 2,
                                py: 2,
                                px: 2.5,
                                backgroundColor: alpha('#26A69A', 0.12),
                                borderRadius: 3,
                                border: `2px solid ${alpha('#26A69A', 0.3)}`,
                                boxShadow: `0 2px 6px ${alpha('#26A69A', 0.15)}`
                              }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 500, 
                                  color: '#26A69A',
                                  fontSize: '1.15rem',
                                  letterSpacing: '0.3px'
                                }}>
                                  Proforma Invoice Workflow
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                      const newPermissions = { ...roleForm.permissions };
                                      const workflowPermissions = permissions.filter(p => p.name && (
                                        p.name.includes('sales.purchase_orders.order_creation') ||
                                        p.name.includes('sales.purchase_orders.po_creation') ||
                                        p.name.includes('sales.purchase_orders.delivery_info') ||
                                        p.name.includes('sales.purchase_orders.review_info')
                                      ));
                                      workflowPermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: true,
                                          write: true,
                                          delete: true
                                        };
                                      });
                                      setRoleForm({ ...roleForm, permissions: newPermissions });
                                    }}
                                    sx={{
                                      fontSize: '0.7rem',
                                      px: 0.75,
                                      py: 0.25,
                                      minWidth: 'auto',
                                      color: '#f44336',
                                      fontWeight: 500,
                                      '&:hover': {
                                        bgcolor: alpha('#f44336', 0.05)
                                      }
                                    }}
                                  >
                                    All
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                      const newPermissions = { ...roleForm.permissions };
                                      const workflowPermissions = permissions.filter(p => p.name && (
                                        p.name.includes('sales.purchase_orders.order_creation') ||
                                        p.name.includes('sales.purchase_orders.po_creation') ||
                                        p.name.includes('sales.purchase_orders.delivery_info') ||
                                        p.name.includes('sales.purchase_orders.review_info')
                                      ));
                                      workflowPermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: false,
                                          write: false,
                                          delete: false
                                        };
                                      });
                                      setRoleForm({ ...roleForm, permissions: newPermissions });
                                    }}
                                    sx={{
                                      fontSize: '0.7rem',
                                      px: 0.75,
                                      py: 0.25,
                                      minWidth: 'auto',
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.grey[500], 0.05)
                                      }
                                    }}
                                  >
                                    None
                                  </Button>
                                </Box>
                              </Box>
                              
                              <ProformaInvoiceWorkflowPermissions
                                permissions={permissions}
                                roleForm={roleForm}
                                setRoleForm={setRoleForm}
                              />
                            </Box>

                            {/* Delivery Confirmation Workflow sub-subsection */}
                            <Box sx={{ mb: 2, mt: 3, ml: 2 }} id="purchase-order-confirmation-section">
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 2,
                                py: 2,
                                px: 2.5,
                                backgroundColor: alpha('#9C27B0', 0.12),
                                borderRadius: 3,
                                border: `2px solid ${alpha('#9C27B0', 0.3)}`,
                                boxShadow: `0 2px 6px ${alpha('#9C27B0', 0.15)}`
                              }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 500, 
                                  color: '#9C27B0',
                                  fontSize: '1.15rem',
                                  letterSpacing: '0.3px'
                                }}>
                                  Delivery Confirmation Workflow
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                      const newPermissions = { ...roleForm.permissions };
                                      const confirmationPermissions = permissions.filter(p => p.name && (
                                        p.name.includes('sales.purchase_orders.delivery_documents') ||
                                        p.name.includes('sales.purchase_orders.inventory_usage') ||
                                        p.name.includes('sales.purchase_orders.invoice_payment') ||
                                        p.name.includes('sales.purchase_orders.delivery_details') ||
                                        p.name.includes('sales.purchase_orders.final_approval')
                                      ));
                                      confirmationPermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: true,
                                          write: true,
                                          delete: true
                                        };
                                      });
                                      setRoleForm({ ...roleForm, permissions: newPermissions });
                                    }}
                                    sx={{
                                      fontSize: '0.7rem',
                                      px: 0.75,
                                      py: 0.25,
                                      minWidth: 'auto',
                                      color: '#f44336',
                                      fontWeight: 500,
                                      '&:hover': {
                                        bgcolor: alpha('#f44336', 0.05)
                                      }
                                    }}
                                  >
                                    All
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                      const newPermissions = { ...roleForm.permissions };
                                      const confirmationPermissions = permissions.filter(p => p.name && (
                                        p.name.includes('sales.purchase_orders.delivery_documents') ||
                                        p.name.includes('sales.purchase_orders.inventory_usage') ||
                                        p.name.includes('sales.purchase_orders.invoice_payment') ||
                                        p.name.includes('sales.purchase_orders.delivery_details') ||
                                        p.name.includes('sales.purchase_orders.final_approval')
                                      ));
                                      confirmationPermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: false,
                                          write: false,
                                          delete: false
                                        };
                                      });
                                      setRoleForm({ ...roleForm, permissions: newPermissions });
                                    }}
                                    sx={{
                                      fontSize: '0.7rem',
                                      px: 0.75,
                                      py: 0.25,
                                      minWidth: 'auto',
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.grey[500], 0.05)
                                      }
                                    }}
                                  >
                                    None
                                  </Button>
                                </Box>
                              </Box>
                              
                              <ProformaInvoiceConfirmationPermissions
                                permissions={permissions}
                                roleForm={roleForm}
                                setRoleForm={setRoleForm}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'products') || (permissionSearchTerm && ('products'.includes(permissionSearchTerm.toLowerCase()) || 'brands'.includes(permissionSearchTerm.toLowerCase()) || 'catalog'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-products" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#FF9800', 0.12),
                          border: `2px solid ${alpha('#FF9800', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#FF9800', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#FF9800',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Products
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const productsPermissions = permissions.filter(p => p.name && p.name.includes('products.') && !p.name.includes('formulations'));
                                productsPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const productsPermissions = permissions.filter(p => p.name && p.name.includes('products.') && !p.name.includes('formulations'));
                                productsPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <ProductsPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'formulations') || (permissionSearchTerm && ('formulations'.includes(permissionSearchTerm.toLowerCase()) || 'formulation'.includes(permissionSearchTerm.toLowerCase()) || 'quality'.includes(permissionSearchTerm.toLowerCase()) || 'qc'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-formulations" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#E91E63', 0.12),
                          border: `2px solid ${alpha('#E91E63', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#E91E63', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#E91E63',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Formulations
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Select all formulation permissions
                                const formulationPages = ['formulations_page', 'formulations_overview', 'formulations_ingredients', 'formulations_procedures', 'formulations_tests', 'formulations_costs', 'formulations_summary'];
                                formulationPages.forEach(pageKey => {
                                  const tabName = pageKey.replace('formulations_', '');
                                  const tabPermissions = permissions.filter(p => 
                                    p.name?.includes(`products.formulations.${tabName}.`)
                                  ) || [];
                                  
                                  tabPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                });
                                
                                // Also select main formulations page permissions
                                const mainViewPermission = permissions.find(p => p.name === 'products.formulations.view');
                                const mainEditPermission = permissions.find(p => p.name === 'products.formulations.edit');
                                const mainDeletePermission = permissions.find(p => p.name === 'products.formulations.delete');
                                
                                if (mainViewPermission) {
                                  newPermissions[mainViewPermission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                }
                                if (mainEditPermission) {
                                  newPermissions[mainEditPermission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                }
                                if (mainDeletePermission) {
                                  newPermissions[mainDeletePermission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                }
                                
                                // Also select special formulation permissions
                                const createPermission = permissions.find(p => p.name === 'products.formulations.create');
                                const approvePermission = permissions.find(p => p.name === 'products.formulations.approve');
                                const deletePermission = permissions.find(p => p.name === 'products.formulations.delete_permanent');
                                
                                if (createPermission) {
                                  newPermissions[createPermission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                }
                                if (approvePermission) {
                                  newPermissions[approvePermission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                }
                                if (deletePermission) {
                                  newPermissions[deletePermission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                }
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Clear all formulation permissions
                                const formulationPages = ['formulations_page', 'formulations_overview', 'formulations_ingredients', 'formulations_procedures', 'formulations_tests', 'formulations_costs', 'formulations_summary'];
                                formulationPages.forEach(pageKey => {
                                  const tabName = pageKey.replace('formulations_', '');
                                  const tabPermissions = permissions.filter(p => 
                                    p.name?.includes(`products.formulations.${tabName}.`)
                                  ) || [];
                                  
                                  tabPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                });
                                
                                // Also clear main formulations page permissions
                                const mainViewPermission = permissions.find(p => p.name === 'products.formulations.view');
                                const mainEditPermission = permissions.find(p => p.name === 'products.formulations.edit');
                                const mainDeletePermission = permissions.find(p => p.name === 'products.formulations.delete');
                                
                                if (mainViewPermission) {
                                  newPermissions[mainViewPermission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                }
                                if (mainEditPermission) {
                                  newPermissions[mainEditPermission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                }
                                if (mainDeletePermission) {
                                  newPermissions[mainDeletePermission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                }
                                
                                // Also clear special formulation permissions
                                const createPermission = permissions.find(p => p.name === 'products.formulations.create');
                                const approvePermission = permissions.find(p => p.name === 'products.formulations.approve');
                                const deletePermission = permissions.find(p => p.name === 'products.formulations.delete_permanent');
                                
                                if (createPermission) {
                                  newPermissions[createPermission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                }
                                if (approvePermission) {
                                  newPermissions[approvePermission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                }
                                if (deletePermission) {
                                  newPermissions[deletePermission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                }
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <FormulationsPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />

                          <Box sx={{ mb: 3, mt: 3, ml: 2 }} id="material-quality-check-section">
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 2,
                              py: 2,
                              px: 2.5,
                              backgroundColor: alpha('#7B1FA2', 0.12),
                              borderRadius: 3,
                              border: `2px solid ${alpha('#7B1FA2', 0.3)}`,
                              boxShadow: `0 2px 6px ${alpha('#7B1FA2', 0.15)}`
                            }}>
                              <Typography variant="h6" sx={{
                                fontWeight: 500,
                                color: '#7B1FA2',
                                fontSize: '1.15rem',
                                letterSpacing: '0.3px'
                              }}>
                                Material Quality Check
                              </Typography>

                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    const qualityCheckPermissions = permissions.filter(p => p.name && (p.name.includes('inventory.supplies.qc_tests') || p.name.includes('inventory.raw_materials.qc_tests')));
                                    qualityCheckPermissions.forEach(permission => {
                                      newPermissions[permission.id] = { read: true, write: true, delete: true };
                                    });
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{ fontSize: '0.7rem', px: 0.75, py: 0.25, minWidth: 'auto', color: '#f44336', fontWeight: 500, '&:hover': { bgcolor: alpha('#f44336', 0.05) } }}
                                >
                                  All
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    const qualityCheckPermissions = permissions.filter(p => p.name && (p.name.includes('inventory.supplies.qc_tests') || p.name.includes('inventory.raw_materials.qc_tests')));
                                    qualityCheckPermissions.forEach(permission => {
                                      newPermissions[permission.id] = { read: false, write: false, delete: false };
                                    });
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{ fontSize: '0.7rem', px: 0.75, py: 0.25, minWidth: 'auto', color: 'text.secondary', fontWeight: 500, '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.05) } }}
                                >
                                  None
                                </Button>
                              </Box>
                            </Box>

                            <MaterialQualityCheckPermissions
                              permissions={permissions}
                              roleForm={roleForm}
                              setRoleForm={setRoleForm}
                            />
                          </Box>
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'inventory') || (permissionSearchTerm && ('inventory'.includes(permissionSearchTerm.toLowerCase()) || 'imports'.includes(permissionSearchTerm.toLowerCase()) || 'exports'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-inventory" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#009688', 0.12),
                          border: `2px solid ${alpha('#009688', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#009688', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#009688',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Inventory
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const inventoryPermissions = permissions.filter(p => p.name && p.name.includes('inventory.') && !p.name.includes('qc_tests'));
                                inventoryPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const inventoryPermissions = permissions.filter(p => p.name && p.name.includes('inventory.') && !p.name.includes('qc_tests'));
                                inventoryPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>

                        <Box sx={{ mb: 3 }} id="inventory-section">
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                            py: 2,
                            px: 2.5,
                            backgroundColor: alpha('#5C6BC0', 0.12),
                            borderRadius: 3,
                            border: `2px solid ${alpha('#5C6BC0', 0.3)}`,
                            boxShadow: `0 2px 6px ${alpha('#5C6BC0', 0.15)}`
                          }}>
                            <Typography variant="h6" sx={{
                              fontWeight: 500,
                              color: '#5C6BC0',
                              fontSize: '1.15rem',
                              letterSpacing: '0.3px'
                            }}>
                              Inventory Page
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const invPerms = permissions.filter(p => p.name && (
                                    p.name === 'inventory.inventory.view' ||
                                    p.name === 'inventory.inventory.edit' ||
                                    p.name === 'inventory.inventory.delete' ||
                                    p.name.startsWith('inventory.inventory.transactions.') ||
                                    p.name.startsWith('inventory.inventory.configurations.')
                                  ));
                                  invPerms.forEach(permission => {
                                    newPermissions[permission.id] = { read: true, write: true, delete: true };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 0.75,
                                  py: 0.25,
                                  minWidth: 'auto',
                                  color: '#f44336',
                                  fontWeight: 500,
                                  '&:hover': { bgcolor: alpha('#f44336', 0.05) }
                                }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const invPerms = permissions.filter(p => p.name && (
                                    p.name === 'inventory.inventory.view' ||
                                    p.name === 'inventory.inventory.edit' ||
                                    p.name === 'inventory.inventory.delete' ||
                                    p.name.startsWith('inventory.inventory.transactions.') ||
                                    p.name.startsWith('inventory.inventory.configurations.')
                                  ));
                                  invPerms.forEach(permission => {
                                    newPermissions[permission.id] = { read: false, write: false, delete: false };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 0.75,
                                  py: 0.25,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.05) }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>

                          <InventoryPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>

                        <Box sx={{ mb: 3 }} id="imports-exports-section">
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 2,
                            mt: 5,
                            py: 2,
                            px: 2.5,
                            backgroundColor: alpha('#00ACC1', 0.12),
                            borderRadius: 3,
                            border: `2px solid ${alpha('#00ACC1', 0.3)}`,
                            boxShadow: `0 2px 6px ${alpha('#00ACC1', 0.15)}`
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 500, 
                              color: '#00ACC1',
                              fontSize: '1.15rem',
                              letterSpacing: '0.3px'
                            }}>
                              Imports & Exports
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const importsExportsPermissions = permissions.filter(p => p.name && (p.name.includes('inventory.imports') || p.name.includes('inventory.exports')));
                                  importsExportsPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 0.75,
                                  py: 0.25,
                                  minWidth: 'auto',
                                  color: '#f44336',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.05)
                                  }
                                }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  const importsExportsPermissions = permissions.filter(p => p.name && (p.name.includes('inventory.imports') || p.name.includes('inventory.exports')));
                                  importsExportsPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 0.75,
                                  py: 0.25,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.grey[500], 0.05)
                                  }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                          
                          <ImportAndExportPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                        
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'manufacturing') || (permissionSearchTerm && ('manufacturing'.includes(permissionSearchTerm.toLowerCase()) || 'planning'.includes(permissionSearchTerm.toLowerCase()) || 'calendar'.includes(permissionSearchTerm.toLowerCase()) || 'requests'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-manufacturing" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#FF4081', 0.12),
                          border: `2px solid ${alpha('#FF4081', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#FF4081', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#FF4081',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Production Planning
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Enable only manufacturing planning and requests permissions
                                const ProductionPlanningPermissions = permissions.filter(p => p.name && (
                                  p.name.includes('production.calendar') || 
                                  p.name.includes('production.batch_requests.') ||
                                  p.name.includes('production.overview')
                                ));
                                ProductionPlanningPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Disable only manufacturing planning and requests permissions
                                const ProductionPlanningPermissions = permissions.filter(p => p.name && (
                                  p.name.includes('production.calendar') || 
                                  p.name.includes('production.batch_requests.') ||
                                  p.name.includes('production.overview')
                                ));
                                ProductionPlanningPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>

                        <ProductionPlanningPermissions
                          permissions={permissions}
                          roleForm={roleForm}
                          setRoleForm={setRoleForm}
                        />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'batch_production') || (permissionSearchTerm && ('batch'.includes(permissionSearchTerm.toLowerCase()) || 'production'.includes(permissionSearchTerm.toLowerCase()) || 'mixing'.includes(permissionSearchTerm.toLowerCase()) || 'logs'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-batch_production" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#00BCD4', 0.12),
                          border: `2px solid ${alpha('#00BCD4', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#00BCD4', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#00BCD4',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Batch Production
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  
                                  // Select all batch production permissions including special ones
                                  const createPermission = permissions.find(p => p.name === 'production.batches.create');
                                  const deletePermission = permissions.find(p => p.name === 'production.batches.delete_permanent');
                                  
                                  if (createPermission) {
                                    newPermissions[createPermission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  }
                                  if (deletePermission) {
                                    newPermissions[deletePermission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  }
                                  
                                  // Select batch production permissions but exclude manufacturing and line production ones
                                  const productionPermissions = permissions.filter(p => p.name && 
                                    p.name.includes('production.') && 
                                    !p.name.includes('production.calendar') && 
                                    !p.name.includes('production.batch_requests.') &&
                                    !p.name.includes('production.overview') &&
                                    !p.name.includes('production.line.')
                                  );
                                  productionPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                  
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: '#f44336',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.05)
                                  }
                                }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  
                                  // Clear all batch production permissions including special ones
                                  const createPermission = permissions.find(p => p.name === 'production.batches.create');
                                  const deletePermission = permissions.find(p => p.name === 'production.batches.delete_permanent');
                                  
                                  if (createPermission) {
                                    newPermissions[createPermission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  }
                                  if (deletePermission) {
                                    newPermissions[deletePermission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  }
                                  
                                  // Clear batch production permissions but exclude manufacturing and line production ones
                                  const productionPermissions = permissions.filter(p => p.name && 
                                    p.name.includes('production.') && 
                                    !p.name.includes('production.calendar') && 
                                    !p.name.includes('production.batch_requests.') &&
                                    !p.name.includes('production.overview') &&
                                    !p.name.includes('production.line.')
                                  );
                                  productionPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                  
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.grey[500], 0.05)
                                  }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                          
                        <BatchProductionPermissions
                          permissions={permissions}
                          roleForm={roleForm}
                          setRoleForm={setRoleForm}
                        />
                        
                        {/* Batch Mixing sub-section */}
                        <Box sx={{ mb: 2, mt: 3, ml: 2 }} id="create-role-subcategory-batch_mixing">
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              mb: 2,
                              py: 2,
                              px: 2.5,
                              backgroundColor: alpha('#FF9800', 0.12),
                              borderRadius: 3,
                              border: `2px solid ${alpha('#FF9800', 0.3)}`,
                              boxShadow: `0 2px 6px ${alpha('#FF9800', 0.15)}`
                            }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 500, 
                                color: '#FF9800',
                                fontSize: '1.15rem',
                                letterSpacing: '0.3px'
                              }}>
                                Batch Mixing
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Select all batch mixing permissions
                                    const batchMixingPages = [
                                      'batch_overview', 
                                      'batch_ingredients', 
                                      'batch_costs', 
                                      'batch_summary'
                                    ];
                                    
                                    batchMixingPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'batch_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_overview'));
                                      } else if (pageKey === 'batch_ingredients') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_ingredients'));
                                      } else if (pageKey === 'batch_costs') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_costs'));
                                      } else if (pageKey === 'batch_summary') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_summary'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: true,
                                          write: true,
                                          delete: true
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: '#f44336',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha('#f44336', 0.05)
                                    }
                                  }}
                                >
                                  All
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Clear all batch mixing permissions
                                    const batchMixingPages = [
                                      'batch_overview', 
                                      'batch_ingredients', 
                                      'batch_costs', 
                                      'batch_summary'
                                    ];
                                    
                                    batchMixingPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'batch_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_overview'));
                                      } else if (pageKey === 'batch_ingredients') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_ingredients'));
                                      } else if (pageKey === 'batch_costs') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_costs'));
                                      } else if (pageKey === 'batch_summary') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_summary'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: false,
                                          write: false,
                                          delete: false
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.grey[500], 0.05)
                                    }
                                  }}
                                >
                                  None
                                </Button>
                              </Box>
                            </Box>
                            
                          <BatchMixingPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                          
                          {/* Batch Logs sub-section */}
                          <Box sx={{ mb: 2, mt: 3, ml: 4 }} id="create-role-subcategory-batch_logs">
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 2,
                                py: 2,
                                px: 2.5,
                                backgroundColor: alpha('#009688', 0.12),
                                borderRadius: 3,
                                border: `2px solid ${alpha('#009688', 0.3)}`,
                                boxShadow: `0 2px 6px ${alpha('#009688', 0.15)}`
                              }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 500, 
                                  color: '#009688',
                                  fontSize: '1.15rem',
                                  letterSpacing: '0.3px'
                                }}>
                                  Batch Log
                                </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Select all batch logs permissions
                                    const batchLogPages = [
                                      'batch_logs_overview', 
                                      'batch_logs_premix', 
                                      'batch_logs_ingredients', 
                                      'batch_logs_qc_checks', 
                                      'batch_logs_deviations', 
                                      'batch_logs_yield', 
                                      'batch_logs_signatures'
                                    ];
                                    
                                    batchLogPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'batch_logs_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.overview'));
                                      } else if (pageKey === 'batch_logs_premix') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.premix'));
                                      } else if (pageKey === 'batch_logs_ingredients') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.ingredients'));
                                      } else if (pageKey === 'batch_logs_qc_checks') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.qc_checks'));
                                      } else if (pageKey === 'batch_logs_deviations') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.deviations'));
                                      } else if (pageKey === 'batch_logs_yield') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.yield'));
                                      } else if (pageKey === 'batch_logs_signatures') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.signatures'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: true,
                                          write: true,
                                          delete: true
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: '#f44336',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha('#f44336', 0.05)
                                    }
                                  }}
                                >
                                  All
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Clear all batch logs permissions
                                    const batchLogPages = [
                                      'batch_logs_overview', 
                                      'batch_logs_premix', 
                                      'batch_logs_ingredients', 
                                      'batch_logs_qc_checks', 
                                      'batch_logs_deviations', 
                                      'batch_logs_yield', 
                                      'batch_logs_signatures'
                                    ];
                                    
                                    batchLogPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'batch_logs_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.overview'));
                                      } else if (pageKey === 'batch_logs_premix') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.premix'));
                                      } else if (pageKey === 'batch_logs_ingredients') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.ingredients'));
                                      } else if (pageKey === 'batch_logs_qc_checks') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.qc_checks'));
                                      } else if (pageKey === 'batch_logs_deviations') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.deviations'));
                                      } else if (pageKey === 'batch_logs_yield') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.yield'));
                                      } else if (pageKey === 'batch_logs_signatures') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_logs.signatures'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: false,
                                          write: false,
                                          delete: false
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.grey[500], 0.05)
                                    }
                                  }}
                                >
                                  None
                                </Button>
                              </Box>
                            </Box>
                            
                            <BatchLogPermissions
                              permissions={permissions}
                              roleForm={roleForm}
                              setRoleForm={setRoleForm}
                            />
                          </Box>
                        </Box>
                        </Box>
                      )}
                        
                      {/* Line Production - standalone category */}
                      {((!permissionSearchTerm && selectedCategory === 'line_production') || (permissionSearchTerm && ('line'.includes(permissionSearchTerm.toLowerCase()) || 'production'.includes(permissionSearchTerm.toLowerCase()) || 'assembly'.includes(permissionSearchTerm.toLowerCase()) || 'log'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-line_production" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#FF6F00', 0.12),
                          border: `2px solid ${alpha('#FF6F00', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#FF6F00', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#FF6F00',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Line Production
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  
                                  // Select all line production permissions including special ones
                                  const createPermission = permissions.find(p => p.name === 'production.line.create');
                                  const deletePermission = permissions.find(p => p.name === 'production.line.delete');
                                  
                                  if (createPermission) {
                                    newPermissions[createPermission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  }
                                  if (deletePermission) {
                                    newPermissions[deletePermission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  }
                                  
                                  // Select line production permissions
                                  const lineProductionPermissions = permissions.filter(p => p.name && 
                                    p.name.includes('production.line.')
                                  );
                                  lineProductionPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                  
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: '#f44336',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.05)
                                  }
                                }}
                              >
                                All
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  const newPermissions = { ...roleForm.permissions };
                                  
                                  // Clear all line production permissions including special ones
                                  const createPermission = permissions.find(p => p.name === 'production.line.create');
                                  const deletePermission = permissions.find(p => p.name === 'production.line.delete');
                                  
                                  if (createPermission) {
                                    newPermissions[createPermission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  }
                                  if (deletePermission) {
                                    newPermissions[deletePermission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  }
                                  
                                  // Clear line production permissions
                                  const lineProductionPermissions = permissions.filter(p => p.name && 
                                    p.name.includes('production.line.')
                                  );
                                  lineProductionPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                  
                                  setRoleForm({ ...roleForm, permissions: newPermissions });
                                }}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1.5,
                                  py: 0.5,
                                  minWidth: 'auto',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.grey[500], 0.05)
                                  }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                          
                        <LineProductionPermissions
                          permissions={permissions}
                          roleForm={roleForm}
                          setRoleForm={setRoleForm}
                        />
                        
                        {/* Line Assembly sub-section */}
                        <Box sx={{ mb: 2, mt: 3, ml: 2 }} id="create-role-subcategory-line_assembly">
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              mb: 2,
                              py: 2,
                              px: 2.5,
                              backgroundColor: alpha('#D32F2F', 0.12),
                              borderRadius: 3,
                              border: `2px solid ${alpha('#D32F2F', 0.3)}`,
                              boxShadow: `0 2px 6px ${alpha('#D32F2F', 0.15)}`
                            }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 500, 
                                color: '#D32F2F',
                                fontSize: '1.15rem',
                                letterSpacing: '0.3px'
                              }}>
                                Line Assembly
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Select all line assembly permissions
                                    const lineAssemblyPages = [
                                      'line_assembly_overview', 
                                      'line_assembly_items', 
                                      'line_assembly_cost', 
                                      'line_assembly_summary'
                                    ];
                                    
                                    lineAssemblyPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'line_assembly_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.overview.'));
                                      } else if (pageKey === 'line_assembly_items') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.items.'));
                                      } else if (pageKey === 'line_assembly_cost') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.cost.'));
                                      } else if (pageKey === 'line_assembly_summary') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.summary.'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: true,
                                          write: true,
                                          delete: true
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: '#f44336',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha('#f44336', 0.05)
                                    }
                                  }}
                                >
                                  All
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Clear all line assembly permissions
                                    const lineAssemblyPages = [
                                      'line_assembly_overview', 
                                      'line_assembly_items', 
                                      'line_assembly_cost', 
                                      'line_assembly_summary'
                                    ];
                                    
                                    lineAssemblyPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'line_assembly_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.overview.'));
                                      } else if (pageKey === 'line_assembly_items') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.items.'));
                                      } else if (pageKey === 'line_assembly_cost') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.cost.'));
                                      } else if (pageKey === 'line_assembly_summary') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.assembly.summary.'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: false,
                                          write: false,
                                          delete: false
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.grey[500], 0.05)
                                    }
                                  }}
                                >
                                  None
                                </Button>
                              </Box>
                            </Box>
                            
                          <LineProductionAssemblyPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                          
                          {/* Line Production Log sub-section */}
                          <Box sx={{ mb: 2, mt: 3, ml: 4 }} id="create-role-subcategory-line_production_log">
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 2,
                                py: 2,
                                px: 2.5,
                                backgroundColor: alpha('#1565C0', 0.12),
                                borderRadius: 3,
                                border: `2px solid ${alpha('#1565C0', 0.3)}`,
                                boxShadow: `0 2px 6px ${alpha('#1565C0', 0.15)}`
                              }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 500, 
                                  color: '#1565C0',
                                  fontSize: '1.15rem',
                                  letterSpacing: '0.3px'
                                }}>
                                  Line Production Log
                                </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Select all line log permissions
                                    const lineLogPages = [
                                      'line_log_overview', 
                                      'line_log_checklist', 
                                      'line_log_supply', 
                                      'line_log_yield', 
                                      'line_log_signatures'
                                    ];
                                    
                                    lineLogPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'line_log_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.overview.'));
                                      } else if (pageKey === 'line_log_checklist') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.checklist.'));
                                      } else if (pageKey === 'line_log_supply') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.supply.'));
                                      } else if (pageKey === 'line_log_yield') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.yield.'));
                                      } else if (pageKey === 'line_log_signatures') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.signatures.'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: true,
                                          write: true,
                                          delete: true
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: '#f44336',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha('#f44336', 0.05)
                                    }
                                  }}
                                >
                                  All
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => {
                                    const newPermissions = { ...roleForm.permissions };
                                    
                                    // Clear all line log permissions
                                    const lineLogPages = [
                                      'line_log_overview', 
                                      'line_log_checklist', 
                                      'line_log_supply', 
                                      'line_log_yield', 
                                      'line_log_signatures'
                                    ];
                                    
                                    lineLogPages.forEach(pageKey => {
                                      let pagePermissions = [];
                                      
                                      if (pageKey === 'line_log_overview') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.overview.'));
                                      } else if (pageKey === 'line_log_checklist') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.checklist.'));
                                      } else if (pageKey === 'line_log_supply') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.supply.'));
                                      } else if (pageKey === 'line_log_yield') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.yield.'));
                                      } else if (pageKey === 'line_log_signatures') {
                                        pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.signatures.'));
                                      }
                                      
                                      pagePermissions.forEach(permission => {
                                        newPermissions[permission.id] = {
                                          read: false,
                                          write: false,
                                          delete: false
                                        };
                                      });
                                    });
                                    
                                    setRoleForm({ ...roleForm, permissions: newPermissions });
                                  }}
                                  sx={{
                                    fontSize: '0.7rem',
                                    px: 0.75,
                                    py: 0.25,
                                    minWidth: 'auto',
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.grey[500], 0.05)
                                    }
                                  }}
                                >
                                  None
                                </Button>
                              </Box>
                            </Box>
                            
                            <LineProductionLogPermissions
                              permissions={permissions}
                              roleForm={roleForm}
                              setRoleForm={setRoleForm}
                            />
                          </Box>
                        </Box>
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'employees') || (permissionSearchTerm && ('employees'.includes(permissionSearchTerm.toLowerCase()) || 'employee'.includes(permissionSearchTerm.toLowerCase()) || 'staff'.includes(permissionSearchTerm.toLowerCase()) || 'personnel'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-employees" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#FFC107', 0.12),
                          border: `2px solid ${alpha('#FFC107', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#FFC107', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#FFC107',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Employees
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Enable all employees permissions
                                const employeesPermissions = permissions.filter(p => p.name && p.name.includes('employees.'));
                                employeesPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Disable all employees permissions
                                const employeesPermissions = permissions.filter(p => p.name && p.name.includes('employees.'));
                                employeesPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <EmployeesPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'schedule') || (permissionSearchTerm && ('schedule'.includes(permissionSearchTerm.toLowerCase()) || 'shifts'.includes(permissionSearchTerm.toLowerCase()) || 'scheduling'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-schedule" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#673AB7', 0.12),
                          border: `2px solid ${alpha('#673AB7', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#673AB7', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#673AB7',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Schedule
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const schedulePermissions = permissions.filter(p => p.name && p.name.includes('schedule.'));
                                schedulePermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const schedulePermissions = permissions.filter(p => p.name && p.name.includes('schedule.'));
                                schedulePermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                          <SchedulePermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>
                      )}

                      {((!permissionSearchTerm && selectedCategory === 'settings') || (permissionSearchTerm && ('settings'.includes(permissionSearchTerm.toLowerCase()) || 'user management'.includes(permissionSearchTerm.toLowerCase()) || 'company'.includes(permissionSearchTerm.toLowerCase()) || 'users'.includes(permissionSearchTerm.toLowerCase()) || 'roles'.includes(permissionSearchTerm.toLowerCase()) || 'permissions'.includes(permissionSearchTerm.toLowerCase())))) && (
                        <Box id="create-role-category-settings" sx={{ mb: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          py: 2.5,
                          px: 3,
                          backgroundColor: alpha('#0277BD', 0.12),
                          border: `2px solid ${alpha('#0277BD', 0.3)}`,
                          borderRadius: 3,
                          boxShadow: `0 2px 8px ${alpha('#0277BD', 0.15)}`
                        }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 500, 
                            color: '#0277BD',
                            fontSize: '1.4rem',
                            letterSpacing: '0.5px'
                          }}>
                            Settings
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Enable all settings permissions
                                const settingsPermissions = permissions.filter(p => p.name && p.name.includes('settings.'));
                                settingsPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                
                                // Disable all settings permissions
                                const settingsPermissions = permissions.filter(p => p.name && p.name.includes('settings.'));
                                settingsPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          ml: 2,
                          py: 2,
                          px: 2.5,
                          backgroundColor: alpha('#9c27b0', 0.12),
                          borderRadius: 3,
                          border: `2px solid ${alpha('#9c27b0', 0.3)}`,
                          boxShadow: `0 2px 6px ${alpha('#9c27b0', 0.15)}`
                        }} id="create-role-subcategory-user_management">
                          <Typography variant="h6" sx={{ 
                            fontWeight: 500, 
                            color: '#9c27b0',
                            fontSize: '1.15rem',
                            letterSpacing: '0.3px'
                          }}>
                            User Management
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const userManagementPermissions = permissions.filter(p => p.name && p.name.includes('settings.user_management.'));
                                userManagementPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: true,
                                    write: true,
                                    delete: true
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const userManagementPermissions = permissions.filter(p => p.name && p.name.includes('settings.user_management.'));
                                userManagementPermissions.forEach(permission => {
                                  newPermissions[permission.id] = {
                                    read: false,
                                    write: false,
                                    delete: false
                                  };
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                        <Box sx={{ mb: 3, ml: 4 }}>
                          <UserManagementPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                        </Box>

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          ml: 2,
                          py: 2,
                          px: 2.5,
                          backgroundColor: alpha('#3F51B5', 0.12),
                          borderRadius: 3,
                          border: `2px solid ${alpha('#3F51B5', 0.3)}`,
                          boxShadow: `0 2px 6px ${alpha('#3F51B5', 0.15)}`
                        }} id="create-role-subcategory-company_settings">
                          <Typography variant="h6" sx={{ 
                            fontWeight: 500, 
                            color: '#3F51B5',
                            fontSize: '1.15rem',
                            letterSpacing: '0.3px'
                          }}>
                            Company Settings
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const companySettingsPages = ['company_information', 'business_address', 'contact_information', 'primary_contact', 'business_settings'];
                                companySettingsPages.forEach(pageKey => {
                                  const settingsPermissions = permissions.filter(p => 
                                    p.name?.includes(`settings.${pageKey}.`)
                                  ) || [];
                                  
                                  settingsPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: true,
                                      write: true,
                                      delete: true
                                    };
                                  });
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: '#f44336',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha('#f44336', 0.05)
                                }
                              }}
                            >
                              All
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const newPermissions = { ...roleForm.permissions };
                                const companySettingsPages = ['company_information', 'business_address', 'contact_information', 'primary_contact', 'business_settings'];
                                companySettingsPages.forEach(pageKey => {
                                  const settingsPermissions = permissions.filter(p => 
                                    p.name?.includes(`settings.${pageKey}.`)
                                  ) || [];
                                  
                                  settingsPermissions.forEach(permission => {
                                    newPermissions[permission.id] = {
                                      read: false,
                                      write: false,
                                      delete: false
                                    };
                                  });
                                });
                                setRoleForm({ ...roleForm, permissions: newPermissions });
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                minWidth: 'auto',
                                color: 'text.secondary',
                                fontWeight: 500,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.grey[500], 0.05)
                                }
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        </Box>
                        
                        <Box sx={{ mb: 3, ml: 4 }}>
                          <CompanySettingsPermissions
                            permissions={permissions}
                            roleForm={roleForm}
                            setRoleForm={setRoleForm}
                          />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Extra white space for better visual separation */}
                  <Box sx={{ height: '40px' }} />

                </Box>
              </StepContent>
            </Step>
          </Stepper>
          
          {/* Always visible action buttons for permissions step */}
          {activeStep === 2 && (
            <Box sx={{ 
              mt: 2,
              pt: 2,
              pb: 2,
              borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
              display: 'flex', 
              justifyContent: 'space-between',
              backgroundColor: 'white',
              position: 'sticky',
              bottom: '20px',
              zIndex: 10
            }}>
              <Button 
                onClick={handleBack}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.2,
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&:hover': {
                    background: alpha(theme.palette.grey[400], 0.1)
                  }
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateRole}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.2,
                  fontWeight: 500,
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  color: 'text.secondary',
                  '&:hover': { 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {isEditMode ? 'Update Role' : 'Create Role'}
              </Button>
            </Box>
          )}
          
          {/* White background to fill the gap below sticky buttons within dialog */}
          {activeStep === 2 && (
            <Box sx={{ 
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '20px',
              backgroundColor: 'white',
              zIndex: 5
            }} />
          )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRoleDialogue;